import React, { useState, useEffect } from "react";
import employeeService from "../../api/services/employeeService";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useUser } from "../../hooks/useUser";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import SafeImage from "../../components/common/SafeImage";

export default function EmployeeProfileSettings() {
  const { data: user, isLoading } = useUser();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [employeeInfo, setEmployeeInfo] = useState({
    firstName: "",
    email: "",
    phoneNumber: "",
    profile_image: "",
  });

  useEffect(() => {
    if (user) {
      fetchEmployeeData();
    }
  }, [user]);

  useEffect(() => {
    if (employeeInfo.profile_image) {
      setProfileImagePreview(
        employeeInfo.profile_image.startsWith("http")
          ? employeeInfo.profile_image
          : (import.meta.env.VITE_API_URL || "http://localhost:8000") +
              employeeInfo.profile_image,
      );
    }
  }, [employeeInfo.profile_image]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const result = await employeeService.getEmployeeById(user.user_id);
      if (result.success) {
        const employee = result.data;
        setEmployeeInfo({
          firstName: employee.user?.name || "",
          email: employee.user?.email || "",
          phoneNumber: employee.user?.phone_no || "",
          profile_image: employee.user?.profile_image || "",
        });
      } else {
        toast.error(result.message || "Failed to fetch employee data");
      }
    } catch (error) {
      toast.error("Failed to fetch employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleInfoChange = (e) => {
    const { name, value } = e.target;
    setEmployeeInfo((prevInfo) => ({ ...prevInfo, [name]: value }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ firstName: true, email: true, phoneNumber: true });
    if (Object.values(validateForm()).length > 0) return;
    try {
      setLoading(true);
      const employeeData = {
        name: employeeInfo.firstName,
        email: employeeInfo.email,
        phone_no: employeeInfo.phoneNumber,
      };
      // Update own profile
      const result = await employeeService.updateEmployee(
        user.user_id,
        employeeData,
      );
      if (result.success) {
        if (profileImage) {
          const imgRes = await employeeService.updateProfileImage(
            user.user_id,
            profileImage,
          );
          if (imgRes.success) {
            toast.success("Profile and image updated successfully");
            fetchEmployeeData();
          } else {
            toast.error(imgRes.message || "Failed to update profile image");
          }
        } else {
          toast.success(result.message || "Profile updated successfully");
          fetchEmployeeData();
        }
      } else {
        const errorMessage = result.errors
          ? Object.values(result.errors).flat().join(", ")
          : result.message || "Operation failed";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(
        "Failed to save employee: " + (error.message || "Unknown error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!employeeInfo.firstName) newErrors.firstName = "Name is required";
    if (!employeeInfo.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeInfo.email))
      newErrors.email = "Please enter a valid email address";
    if (!employeeInfo.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";
    else if (!/^\+?[1-9]\d{7,14}$/.test(employeeInfo.phoneNumber))
      newErrors.phoneNumber =
        "Please enter a valid international phone number (e.g., +1234567890)";
    return newErrors;
  };

  useEffect(() => {
    setErrors(validateForm());
  }, [employeeInfo]);

  // Only show full-page loading on initial fetch
  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="row">
            <div className="col-sm-8 m-auto">
              <div className="card">
                <div className="card-body">
                  <div className="title-header option-title">
                    <h5>Profile Settings</h5>
                  </div>
                  <form
                    className="theme-form theme-form-2 mega-form"
                    onSubmit={handleSubmit}
                  >
                    <div className="row">
                      <div className="mb-4 row align-items-center">
                        <label className="form-label-title col-lg-2 col-md-3 mb-0">
                          Name
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="text"
                            name="firstName"
                            value={employeeInfo.firstName}
                            onChange={handleInfoChange}
                            required
                          />
                          {touched.firstName && errors.firstName && (
                            <div className="text-danger small mt-1">
                              {errors.firstName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Email Address
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <input
                            className="form-control"
                            type="email"
                            name="email"
                            value={employeeInfo.email}
                            onChange={handleInfoChange}
                            required
                          />
                          {touched.email && errors.email && (
                            <div className="text-danger small mt-1">
                              {errors.email}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Phone Number
                        </label>
                        <div className="col-md-9 col-lg-10">
                          <PhoneInput
                            country={"us"}
                            value={employeeInfo.phoneNumber}
                            onChange={(phone) =>
                              setEmployeeInfo((prev) => ({
                                ...prev,
                                phoneNumber: phone,
                              }))
                            }
                            inputProps={{
                              name: "phoneNumber",
                              required: true,
                              className: "form-control",
                              onBlur: () =>
                                setTouched((prev) => ({
                                  ...prev,
                                  phoneNumber: true,
                                })),
                            }}
                            containerStyle={{
                              width: "100%",
                            }}
                            inputStyle={{
                              width: "100%",
                              height: 45,
                              fontSize: "1rem",
                              paddingLeft: "90px",
                            }}
                            dropdownStyle={{
                              zIndex: 9999,
                            }}
                          />
                          {touched.phoneNumber && errors.phoneNumber && (
                            <div className="text-danger small mt-1">
                              {errors.phoneNumber}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-4 row align-items-center">
                        <label className="col-lg-2 col-md-3 col-form-label form-label-title">
                          Profile Image
                        </label>
                        <div className="col-md-9 col-lg-10 d-flex align-items-center">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control"
                            onChange={handleProfileImageChange}
                          />
                          <SafeImage
                            src={
                              profileImagePreview || "/avatar-placeholder.png"
                            }
                            alt="Profile Preview"
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: "50%",
                              marginLeft: 16,
                            }}
                            fallbackSrc="/avatar-placeholder.png"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="card-submit-button">
                      <button
                        className="btn btn-animation ms-auto"
                        type="submit"
                        disabled={loading || Object.values(errors).length > 0}
                        style={{
                          backgroundColor:
                            loading || Object.values(errors).length > 0
                              ? "#6c757d"
                              : "",
                          color:
                            loading || Object.values(errors).length > 0
                              ? "#ffffff"
                              : "",
                          cursor:
                            loading || Object.values(errors).length > 0
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            loading || Object.values(errors).length > 0
                              ? 0.6
                              : 1,
                        }}
                      >
                        {loading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Change Password Form for Employee */}
      <div className="row mt-4">
        <div className="col-sm-8 m-auto">
          <ChangePasswordForm endpoint="/employee/change-password" />
        </div>
      </div>
    </div>
  );
}
